import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {compressPhotoFile} from "@mytour/platform";
import {JARDINIER_FIELD_MAX_LENGTH, MAX_JARDINIER_PHOTOS} from "@mytour/shared";
import {useRef} from "react";
import type {Control, FieldArrayWithId, UseFieldArrayRemove} from "react-hook-form";
import {Controller, useFieldArray} from "react-hook-form";
import {toast} from "react-toastify";
import {FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const step2Schema = z.object({
  photos: z
    .array(
      z.object({
        dataUrl: z.string(),
        caption: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
      })
    )
    .max(MAX_JARDINIER_PHOTOS),
});

export type Step2Form = z.infer<typeof step2Schema>;

type Props = {
  control: Control<Step2Form>;
  disabled: boolean;
};

export function Step2Photos({control, disabled}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {fields, append, remove} = useFieldArray({control, name: "photos"});

  return (
    <>
      <h5 style={{marginBottom: "12px"}}>Photos</h5>

      <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
        {fields.map((field, index) => (
          <PhotoRow
            key={field.id}
            field={field}
            index={index}
            control={control}
            disabled={disabled}
            onRemove={remove}
          />
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{display: "none"}}
        onChange={handleFileSelected}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || fields.length >= MAX_JARDINIER_PHOTOS}
        style={{
          marginTop: "10px",
          border: "2px dashed var(--mt-color-action)",
          borderRadius: "6px",
          padding: "12px",
          textAlign: "center",
          color: "var(--mt-color-action)",
          fontWeight: 600,
          background: "transparent",
          width: "100%",
        }}
      >
        ＋ Ajouter une photo
      </button>
      <div style={{fontSize: "12px", color: "#6c757d", textAlign: "right", marginTop: "4px"}}>
        {fields.length} photos sur {MAX_JARDINIER_PHOTOS}
      </div>
    </>
  );

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || fields.length >= MAX_JARDINIER_PHOTOS) {
      return;
    }
    try {
      const dataUrl = await compressPhotoFile(file, {maxLongSide: 1600});
      append({dataUrl, caption: ""});
    } catch {
      toast.error("Erreur lors de l'ajout de la photo.");
    }
  }
}

type PhotoRowProps = {
  field: FieldArrayWithId<Step2Form, "photos", "id">;
  index: number;
  control: Control<Step2Form>;
  disabled: boolean;
  onRemove: UseFieldArrayRemove;
};

function PhotoRow({field, index, control, disabled, onRemove}: PhotoRowProps) {
  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        alignItems: "center",
        border: "1px solid #dee2e6",
        borderRadius: "6px",
        padding: "8px",
      }}
    >
      <img
        src={field.dataUrl}
        alt=""
        style={{
          width: "56px",
          height: "56px",
          borderRadius: "5px",
          objectFit: "cover",
          flex: "none",
        }}
      />
      <FormGroup floating style={{flex: 1, marginBottom: 0}}>
        <Controller
          name={`photos.${index}.caption`}
          control={control}
          render={({field: captionField}) => (
            <Input
              {...captionField}
              type="text"
              id={`field-photo-caption-${index}`}
              placeholder="Titre ou légende"
              disabled={disabled}
              maxLength={JARDINIER_FIELD_MAX_LENGTH}
              value={captionField.value ?? ""}
            />
          )}
        />
        <Label for={`field-photo-caption-${index}`}>Titre ou légende</Label>
      </FormGroup>
      <button
        type="button"
        aria-label="Supprimer la photo"
        onClick={() => onRemove(index)}
        disabled={disabled}
        style={{
          border: "none",
          background: "transparent",
          color: "var(--bs-danger)",
          fontSize: "18px",
          padding: "0 4px",
          flex: "none",
        }}
      >
        <FontAwesomeIcon icon="trash" />
      </button>
    </div>
  );
}
